import { DOCUMENT } from '@angular/common';
import { ApplicationRef, Inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ENV_PROD_BUILD } from '@y42/environment';
import { ErrorHandlerService } from '@y42/error-handling';
import { concat, interval, of } from 'rxjs';
import { catchError, filter, first, map, switchMap, tap, throttleTime } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ServiceWorkerService {
  constructor(
    private readonly appRef: ApplicationRef,
    private readonly swUpdate: SwUpdate,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(ENV_PROD_BUILD) private readonly production: boolean,
    private readonly errorHanderService: ErrorHandlerService,
  ) {
    this.initialize();
  }

  private initialize() {
    this.periodicallyCheckForUpdates().subscribe();
    this.notifyWhenUpdateAvailable().subscribe();
  }

  private notifyWhenUpdateAvailable() {
    const updatesAvailable$ = this.swUpdate.versionUpdates.pipe(
      filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
    );

    return updatesAvailable$.pipe(
      throttleTime(5000),
      map(() => confirm('A newer version is now available. Do you want to load that version?')),
      filter((confirmed) => confirmed),
      tap(() => this.swUpdate.activateUpdate().then(() => this.document.location.reload())),
    );
  }

  private periodicallyCheckForUpdates() {
    // Checking for updates every 2 hours
    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable));
    const everyTwoHours$ = interval(2 * 60 * 60 * 1000);
    const everyTwoHoursOnceAppIsStable$ = concat(appIsStable$, everyTwoHours$);

    return everyTwoHoursOnceAppIsStable$.pipe(
      switchMap(() => this.swUpdate.checkForUpdate()),
      catchError((error: unknown) => {
        if (this.production) {
          this.errorHanderService.handleError(error);
        }
        return of({});
      }),
    );
  }
}
